/**
 * Who is actually selling. Every result in Talab comes from a real business,
 * and a shopper deciding between two identical Prados is really deciding
 * between two sellers — so the seller has to be on the card, not buried in a
 * detail page.
 */
export interface Merchant {
  name: string
  /** Letter-mark, used when the merchant has no uploaded logo. */
  initials: string
  /** The mark's ground colour. */
  colour: string
  /** An uploaded logo wins over the letter-mark. */
  logo?: string
  verified: boolean
  /** "Car dealer", "Rental company", "Perfume atelier" — read under the name. */
  kind?: string
  rating?: string
}

const REGISTRY: Record<string, Partial<Merchant>> = {
  'Al Quoz Motors': {
    colour: '#1E3A5F',
    verified: true,
    kind: 'Car dealer',
    rating: '4.8',
  },
  'Emirates Auto': {
    colour: '#2F5D50',
    verified: true,
    kind: 'Car dealer',
    rating: '4.6',
  },
  EasyLease: {
    colour: '#4C7FC1',
    verified: true,
    kind: 'Vehicle rental',
    rating: '4.9',
    logo: 'https://easylease.ae/wp-content/uploads/2023/05/Easylease-Logo.png',
  },
  'Circuit Hub': {
    colour: '#1F2937',
    verified: true,
    kind: 'Electronics store',
    rating: '4.7',
  },
  'Pixel Point': {
    colour: '#2B4C6F',
    verified: true,
    kind: 'Electronics store',
    rating: '4.6',
  },
  'Volt Electronics': {
    colour: '#2F5D50',
    verified: true,
    kind: 'Electronics store',
    rating: '4.5',
  },
  'Prime Estates': {
    colour: '#8A6A3B',
    verified: true,
    kind: 'Real estate agency',
    rating: '4.7',
  },
  'Marina Realty': {
    colour: '#2B4C6F',
    verified: true,
    kind: 'Real estate agency',
    rating: '4.5',
  },
  'Atelier Nine': {
    colour: '#3A3A3C',
    verified: true,
    kind: 'Fashion boutique',
    rating: '4.8',
  },
  'Maison Layal': {
    colour: '#6B2F45',
    verified: true,
    kind: 'Modest couture',
    rating: '4.9',
  },
  'Studio Form': {
    colour: '#2A3247',
    verified: true,
    kind: 'Footwear & knitwear',
    rating: '4.7',
  },
  'Maison Aroma': {
    colour: '#7A5230',
    verified: true,
    kind: 'Perfume atelier',
    rating: '4.9',
  },
  'Attar House': {
    colour: '#8C4A5A',
    verified: true,
    kind: 'Fragrance house',
    rating: '4.6',
  },
  'Atelier Maison': {
    colour: '#6B5B47',
    verified: true,
    kind: 'Furniture atelier',
    rating: '4.8',
  },
  'Nordic Rooms': {
    colour: '#3C4A52',
    verified: true,
    kind: 'Furniture showroom',
    rating: '4.7',
  },
  'Studio Habitat': {
    colour: '#4A5240',
    verified: true,
    kind: 'Furniture workshop',
    rating: '4.6',
  },
  'Burger King': {
    colour: '#D62300',
    verified: true,
    kind: 'Quick-service restaurant',
    rating: '4.5',
  },
  'Prime Burger': {
    colour: '#B4552D',
    verified: true,
    kind: 'Burger kitchen',
    rating: '4.8',
  },
  'Ember House': {
    colour: '#3E3A36',
    verified: true,
    kind: 'Grill restaurant',
    rating: '4.9',
  },
  'Green Fork': {
    colour: '#3F6B4A',
    verified: true,
    kind: 'Plant-forward kitchen',
    rating: '4.6',
  },
  'Corner Diner': {
    colour: '#9A3B3B',
    verified: true,
    kind: 'Diner',
    rating: '4.5',
  },
}

/** Stable, pleasant ground colour for merchants not in the registry. */
const FALLBACK = [
  '#1E3A5F',
  '#2F5D50',
  '#6B2F45',
  '#7A5230',
  '#2A3247',
  '#8A6A3B',
]

const initialsOf = (name: string) =>
  name
    .replace(/[^A-Za-z\s]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('')

export function merchantFor(
  rawName: string,
  fallbackKind?: string,
): Merchant {
  const name = (rawName || 'Talab Seller').split(' · ')[0].trim()
  const known = REGISTRY[name]
  const hash = [...name].reduce((n, c) => n + c.charCodeAt(0), 0)
  return {
    name,
    initials: initialsOf(name) || 'T',
    colour: known?.colour ?? FALLBACK[hash % FALLBACK.length],
    logo: known?.logo,
    verified: known?.verified ?? false,
    kind: known?.kind ?? fallbackKind,
    rating: known?.rating,
  }
}

/**
 * Car and property results carry an area rather than a business name, so the
 * seller is resolved per listing.
 */
export const LISTING_SELLERS: Record<string, string> = {
  '1': 'Al Quoz Motors',
  '2': 'Private Seller',
  '3': 'Emirates Auto',
  '4': 'Al Quoz Motors',
  '5': 'Emirates Auto',
  '6': 'Private Seller',
  '7': 'Al Quoz Motors',
  p1: 'Prime Estates',
  p2: 'Marina Realty',
  p3: 'Prime Estates',
  p4: 'Marina Realty',
  p5: 'Prime Estates',
  p6: 'Marina Realty',
  e1: 'Circuit Hub',
  e2: 'Pixel Point',
  e3: 'Circuit Hub',
  e4: 'Pixel Point',
  e5: 'Volt Electronics',
  e6: 'Circuit Hub',
}
